import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Shield, Users, Eye, EyeOff, ChevronDown, ArrowLeft, Mail, Phone, KeyRound, CheckCircle, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import HOSLogo from '../../components/HOSLogo';

const ROLES = [
  { value: 'master_admin', label: 'Master Admin', desc: 'Full system access & user management', icon: Shield, color: '#B8A830', bg: '#FFFBEB' },
  { value: 'manager',      label: 'Web Manager',  desc: 'Limited access as assigned by Master Admin', icon: Users, color: '#4F8EF7', bg: '#EFF6FF' },
];

// ── OTP Input — 6 individual boxes ──────────────────────────────────────────
function OtpInput({ value, onChange }) {
  const inputs = useRef([]);
  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      const next = digits.map((d, idx) => idx === i ? '' : d).join('');
      onChange(next);
      if (i > 0) inputs.current[i - 1]?.focus();
    }
  };

  const handleChange = (i, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = digits.map((d, idx) => idx === i ? char : d).join('');
    onChange(next);
    if (char && i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input key={i}
          ref={el => inputs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1}
          value={d}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-xl focus:outline-none transition-colors
            ${d ? 'border-yellow-400 bg-yellow-50 text-gray-800' : 'border-gray-200 bg-white text-gray-800'}
            focus:border-yellow-500`}
        />
      ))}
    </div>
  );
}

export default function AdminLogin() {
  const [loginRole, setLoginRole] = useState('master_admin');
  const [roleOpen, setRoleOpen]   = useState(false);
  // view: 'login' | 'forgot-choice' | 'forgot-username' | 'forgot-password' | 'otp' | 'reset'
  const [view, setView]           = useState('login');
  const [form, setForm]           = useState({ email: '', password: '' });
  const [forgotType, setForgotType] = useState('password'); // 'username' | 'password'
  const [otpMethod, setOtpMethod] = useState('email');      // 'email' | 'phone'
  const [identifier, setIdentifier] = useState('');         // email or phone for OTP
  const [usernameEmail, setUsernameEmail] = useState('');   // for forgot-username
  const [otp, setOtp]             = useState('');
  const [resetPwd, setResetPwd]   = useState({ password: '', confirm: '' });
  const [showPwd, setShowPwd]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [devOtp, setDevOtp]       = useState('');
  const timerRef = useRef(null);
  const { login } = useAuth();
  const navigate  = useNavigate();

  const selectedRole = ROLES.find(r => r.value === loginRole);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(t => t - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [resendTimer]);

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (loginRole === 'master_admin' && user.role !== 'master_admin') {
        toast.error('This account does not have Master Admin access');
        return;
      }
      if (loginRole === 'manager' && user.role === 'master_admin') {
        toast.error('Please select Master Admin login for this account');
        return;
      }
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 1: Request OTP ── */
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { toast.error(`Enter your ${otpMethod === 'email' ? 'email address' : 'phone number'}`); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { identifier: identifier.trim() });
      if (res.data.devOtp) {
        setDevOtp(res.data.devOtp);
        toast.info(`Dev mode — OTP: ${res.data.devOtp}`, { autoClose: false });
      } else {
        toast.success('OTP sent to your registered email');
      }
      setOtp('');
      setResendTimer(60);
      setView('otp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  /* ── Forgot Username ── */
  const handleForgotUsername = async (e) => {
    e.preventDefault();
    if (!usernameEmail.trim()) { toast.error('Enter your registered email address'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-username', { email: usernameEmail.trim() });
      if (res.data.devInfo) {
        toast.info(`Dev mode — Name: ${res.data.devInfo.name}, Email: ${res.data.devInfo.email}`, { autoClose: false });
      } else {
        toast.success('Account details sent to your registered email');
      }
      setView('login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send account details');
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2: Verify OTP ── */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.replace(/\D/g, '').length !== 6) { toast.error('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      await api.post('/auth/verify-otp', { identifier: identifier.trim(), otp: otp.trim() });
      toast.success('OTP verified');
      setResetPwd({ password: '', confirm: '' });
      setView('reset');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 3: Reset Password ── */
  const handleReset = async (e) => {
    e.preventDefault();
    if (resetPwd.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (resetPwd.password !== resetPwd.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { identifier: identifier.trim(), password: resetPwd.password });
      toast.success('Password reset successfully. Please sign in.');
      setDevOtp('');
      setView('login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { identifier: identifier.trim() });
      if (res.data.devOtp) {
        setDevOtp(res.data.devOtp);
        toast.info(`Dev mode — new OTP: ${res.data.devOtp}`, { autoClose: false });
      } else {
        toast.success('New OTP sent');
      }
      setOtp('');
      setResendTimer(60);
    } catch (err) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const pwdSteps = [
    { label: otpMethod === 'email' ? 'Email' : 'Phone', done: view === 'otp' || view === 'reset' },
    { label: 'OTP',   done: view === 'reset' },
    { label: 'Reset', done: false },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, #B8A830 0%, transparent 50%), radial-gradient(circle at 75% 75%, #4F8EF7 0%, transparent 50%)' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <HOSLogo size={44} />
          <div>
            <h1 className="text-4xl font-serif text-white mb-4 leading-tight">
              Welcome to the<br />
              <span style={{ color: '#B8A830' }}>Admin Portal</span>
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed mb-8">
              Secure access to manage your House of Sample platform. Sign in with your credentials to continue.
            </p>
            <div className="space-y-3">
              {ROLES.map(r => (
                <div key={r.value} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${r.color}20` }}>
                    <r.icon size={16} style={{ color: r.color }} />
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">{r.label}</p>
                    <p className="text-gray-400 text-xs">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-gray-600 text-xs">© {new Date().getFullYear()} House of Sample. All rights reserved.</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8"><HOSLogo size={44} /></div>

          {/* ── LOGIN ── */}
          {view === 'login' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-1">Sign In</h2>
              <p className="text-sm text-gray-400 mb-6">Select your role and enter credentials</p>

              {/* Role selector */}
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Login As</label>
                <div className="relative">
                  <button type="button" onClick={() => setRoleOpen(o => !o)}
                    className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-xl bg-white hover:border-yellow-400 transition-colors text-left">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: selectedRole.bg }}>
                      <selectedRole.icon size={16} style={{ color: selectedRole.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">{selectedRole.label}</p>
                      <p className="text-xs text-gray-400">{selectedRole.desc}</p>
                    </div>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${roleOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {roleOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                      {ROLES.map(r => (
                        <button key={r.value} type="button"
                          onClick={() => { setLoginRole(r.value); setRoleOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${loginRole === r.value ? 'bg-yellow-50' : ''}`}>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: r.bg }}>
                            <r.icon size={16} style={{ color: r.color }} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{r.label}</p>
                            <p className="text-xs text-gray-400">{r.desc}</p>
                          </div>
                          {loginRole === r.value && <span className="ml-auto w-2 h-2 rounded-full bg-yellow-500" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" required value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="admin@example.com"
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
                  <div className="relative">
                    <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type={showPwd ? 'text' : 'password'} required value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => { setView('forgot-choice'); }}
                    className="text-xs text-yellow-600 hover:text-yellow-700 font-medium">
                    Forgot username / password?
                  </button>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: loading ? '#ccc' : 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                  {loading ? 'Signing in…' : `Sign In as ${selectedRole.label}`}
                </button>
              </form>
              <p className="text-center text-xs text-gray-400 mt-6">
                <Link to="/" className="hover:text-yellow-600 transition-colors flex items-center justify-center gap-1">
                  <ArrowLeft size={11} /> Back to website
                </Link>
              </p>
              <p className="text-center text-xs text-gray-400 mt-3">
                No account?{' '}
                <Link to="/admin/register" className="text-yellow-600 hover:text-yellow-700 font-semibold transition-colors">
                  Create Master Admin Account
                </Link>
              </p>
            </div>
          )}

          {/* ── FORGOT CHOICE ── */}
          {view === 'forgot-choice' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <button onClick={() => setView('login')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-6">
                <ArrowLeft size={13} /> Back to login
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-1">Account Recovery</h2>
              <p className="text-sm text-gray-400 mb-8">What do you need help with?</p>
              <div className="space-y-3">
                <button onClick={() => { setForgotType('username'); setUsernameEmail(''); setView('forgot-username'); }}
                  className="w-full flex items-center gap-4 p-4 border-2 border-gray-100 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-all text-left group">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-yellow-100">
                    <User size={18} className="text-blue-500 group-hover:text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Forgot Username</p>
                    <p className="text-xs text-gray-400">We'll send your account details to your registered email</p>
                  </div>
                </button>
                <button onClick={() => { setForgotType('password'); setIdentifier(''); setOtpMethod('email'); setView('forgot-password'); }}
                  className="w-full flex items-center gap-4 p-4 border-2 border-gray-100 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-all text-left group">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0 group-hover:bg-yellow-100">
                    <KeyRound size={18} className="text-amber-500 group-hover:text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Forgot Password</p>
                    <p className="text-xs text-gray-400">Reset via OTP sent to your email or phone number</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* ── FORGOT USERNAME ── */}
          {view === 'forgot-username' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <button onClick={() => setView('forgot-choice')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-6">
                <ArrowLeft size={13} /> Back
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-1">Forgot Username?</h2>
              <p className="text-sm text-gray-400 mb-6">Enter your registered email address. We'll send your account details.</p>
              <form onSubmit={handleForgotUsername} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Registered Email Address</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" required value={usernameEmail}
                      onChange={e => setUsernameEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                  {loading ? 'Sending…' : 'Send Account Details'}
                </button>
              </form>
            </div>
          )}

          {/* ── FORGOT PASSWORD — choose email or phone ── */}
          {view === 'forgot-password' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <button onClick={() => setView('forgot-choice')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-6">
                <ArrowLeft size={13} /> Back
              </button>

              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-6">
                {pwdSteps.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <div className={`flex items-center gap-1.5 text-xs font-semibold ${i === 0 ? 'text-yellow-600' : 'text-gray-300'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-400'}`}>{i + 1}</span>
                      {s.label}
                    </div>
                    {i < pwdSteps.length - 1 && <div className="flex-1 h-px bg-gray-100" />}
                  </React.Fragment>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-1">Forgot Password?</h2>
              <p className="text-sm text-gray-400 mb-5">Choose how to receive your OTP.</p>

              {/* Email / Phone toggle */}
              <div className="flex gap-2 mb-5">
                <button type="button" onClick={() => { setOtpMethod('email'); setIdentifier(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all
                    ${otpMethod === 'email' ? 'border-yellow-400 bg-yellow-50 text-yellow-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  <Mail size={14} /> Email
                </button>
                <button type="button" onClick={() => { setOtpMethod('phone'); setIdentifier(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all
                    ${otpMethod === 'phone' ? 'border-yellow-400 bg-yellow-50 text-yellow-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  <Phone size={14} /> Phone
                </button>
              </div>

              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    {otpMethod === 'email' ? 'Registered Email Address' : 'Registered Phone Number'}
                  </label>
                  <div className="relative">
                    {otpMethod === 'email'
                      ? <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      : <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    }
                    <input
                      type={otpMethod === 'email' ? 'email' : 'tel'}
                      required
                      value={identifier}
                      onChange={e => setIdentifier(e.target.value)}
                      placeholder={otpMethod === 'email' ? 'your@email.com' : '+91 98765 43210'}
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {otpMethod === 'email'
                      ? 'OTP will be sent to this email address'
                      : 'OTP will be sent to your registered email (linked to this phone)'}
                  </p>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                  {loading ? 'Sending OTP…' : 'Send OTP'}
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-4 text-center">
                Remember your password?{' '}
                <button onClick={() => setView('login')} className="text-yellow-600 font-medium hover:underline">Sign in</button>
              </p>
            </div>
          )}

          {/* ── STEP 2: ENTER OTP ── */}
          {view === 'otp' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <button onClick={() => setView('forgot-password')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-6">
                <ArrowLeft size={13} /> Change {otpMethod}
              </button>

              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-6">
                {pwdSteps.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <div className={`flex items-center gap-1.5 text-xs font-semibold
                      ${i === 0 ? 'text-emerald-500' : i === 1 ? 'text-yellow-600' : 'text-gray-300'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold
                        ${i === 0 ? 'bg-emerald-500 text-white' : i === 1 ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                        {i === 0 ? <CheckCircle size={12} /> : i + 1}
                      </span>
                      {s.label}
                    </div>
                    {i < pwdSteps.length - 1 && <div className="flex-1 h-px bg-gray-100" />}
                  </React.Fragment>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-1">Enter OTP</h2>
              <p className="text-sm text-gray-400 mb-1">
                OTP sent to registered email for <span className="font-medium text-gray-700">{identifier}</span>
              </p>
              <p className="text-xs text-gray-400 mb-6">Valid for 10 minutes.</p>

              {devOtp && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                  Dev mode — OTP: <strong className="font-mono text-base tracking-widest">{devOtp}</strong>
                </div>
              )}

              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <OtpInput value={otp} onChange={setOtp} />
                <button type="submit" disabled={loading || otp.replace(/\D/g,'').length !== 6}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                  {loading ? 'Verifying…' : 'Verify OTP'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button onClick={handleResend} disabled={resendTimer > 0 || loading}
                  className="text-xs text-yellow-600 hover:text-yellow-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed">
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: NEW PASSWORD ── */}
          {view === 'reset' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-6">
                {pwdSteps.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <div className={`flex items-center gap-1.5 text-xs font-semibold
                      ${i < 2 ? 'text-emerald-500' : 'text-yellow-600'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold
                        ${i < 2 ? 'bg-emerald-500 text-white' : 'bg-yellow-500 text-white'}`}>
                        {i < 2 ? <CheckCircle size={12} /> : i + 1}
                      </span>
                      {s.label}
                    </div>
                    {i < pwdSteps.length - 1 && <div className="flex-1 h-px bg-gray-100" />}
                  </React.Fragment>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-1">Set New Password</h2>
              <p className="text-sm text-gray-400 mb-6">OTP verified. Choose a strong new password.</p>

              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">New Password</label>
                  <div className="relative">
                    <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type={showNew ? 'text' : 'password'} required value={resetPwd.password}
                      onChange={e => setResetPwd(p => ({ ...p, password: e.target.value }))}
                      placeholder="Min 6 characters"
                      className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
                    <button type="button" onClick={() => setShowNew(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type={showConfirm ? 'text' : 'password'} required value={resetPwd.confirm}
                      onChange={e => setResetPwd(p => ({ ...p, confirm: e.target.value }))}
                      placeholder="Repeat new password"
                      className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {resetPwd.confirm && resetPwd.password !== resetPwd.confirm && (
                    <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                  )}
                  {resetPwd.confirm && resetPwd.password === resetPwd.confirm && resetPwd.password.length >= 6 && (
                    <p className="text-emerald-500 text-xs mt-1 flex items-center gap-1"><CheckCircle size={11} /> Passwords match</p>
                  )}
                </div>
                <button type="submit" disabled={loading || resetPwd.password !== resetPwd.confirm || resetPwd.password.length < 6}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                  {loading ? 'Resetting…' : 'Reset Password'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
